/**
 * MONSTER TOWN — WHAT YOU CAN LEAVE ON A DOORSTEP. VISIT CREW (src/visit/**).
 *
 * DATA, not engine. A new present is a row in `PICKABLE`; a new picture for
 * something a later crew puts in the bag is a row in `PRESENT_ART`. Neither
 * needs a line of logic changed anywhere.
 *
 * TWO SOURCES OF PRESENTS, AND WHY THERE ARE TWO:
 *
 *  1. YOUR BAG. Anything in `inventory` can be given away — a cooked dish, a
 *     caught fish, a flower out of the garden. This is the real one, and it
 *     arrives for free as the cooking / fishing / garden crews land: this file
 *     only supplies the PICTURE, never the permission.
 *
 *  2. THINGS YOU CAN JUST PICK. `PICKABLE` costs nothing and is always there.
 *     LAW #2 is the reason: on the day this ships a kid's bag is empty, and a
 *     "leave a present" button that can only ever say "you have nothing" is a
 *     fail state wearing a smile. A 3-year-old must always be able to give
 *     their brother something.
 *
 * Nothing here is bought, nothing is spent, and nothing can run out.
 */

import type { PaletteName } from '../core/palette'

export interface PresentDef {
  /** The item id that lands in the RECEIVER's bag. Stable forever. */
  readonly id: string
  /** Dev label. Never shown to a kid (LAW #4). */
  readonly devName: string
  /** Picture name. `visit` = this crew's own set, `ui` = the HUD's set. */
  readonly icon: string
  readonly iconSet: 'visit' | 'ui'
  readonly tint?: PaletteName
  readonly tint2?: PaletteName
}

/**
 * FREE, ALWAYS THERE. Four of them, because a row of four fits one shelf on the
 * hand-me-down iPad without scrolling and four choices is a choice; ten is a
 * decision.
 */
export const PICKABLE: readonly PresentDef[] = [
  { id: 'present_flower', devName: 'a flower', icon: 'plant', iconSet: 'ui', tint: 'flowerPink' },
  { id: 'present_star', devName: 'a star', icon: 'star', iconSet: 'ui', tint: 'monsterSunny' },
  { id: 'present_heart', devName: 'a heart', icon: 'heart', iconSet: 'ui', tint: 'flowerRed' },
  { id: 'present_cake', devName: 'a little cake', icon: 'cake', iconSet: 'ui', tint: 'iceStrawberry' },
]

/**
 * PICTURE FOR A THING OUT OF THE BAG. Keyed by item id, first match on a
 * fragment wins, so a whole family of ids that a later crew invents
 * (`dish_fruit_bowl`, `dish_pancakes`) draws sensibly without this file having
 * heard of it. An id nothing matches draws as a present — never as nothing.
 */
const ART_BY_FRAGMENT: readonly (readonly [string, string, 'visit' | 'ui', PaletteName])[] = [
  ['present_flower', 'plant', 'ui', 'flowerPink'],
  ['present_star', 'star', 'ui', 'monsterSunny'],
  ['present_heart', 'heart', 'ui', 'flowerRed'],
  ['present_cake', 'cake', 'ui', 'iceStrawberry'],
  ['dish_', 'plate', 'ui', 'plate'],
  ['recipe_', 'cake', 'ui', 'iceVanilla'],
  ['ing_fruit', 'cherry', 'ui', 'cherry'],
  ['ing_', 'plant', 'ui', 'leafLight'],
  ['fish_', 'water', 'ui', 'water'],
  ['bug_', 'sparkle', 'ui', 'monsterLime'],
  ['flower', 'plant', 'ui', 'flowerPurple'],
  ['cake', 'cake', 'ui', 'iceStrawberry'],
  ['coin', 'coin', 'ui', 'flowerYellow'],
]

export interface PresentArt {
  readonly icon: string
  readonly iconSet: 'visit' | 'ui'
  readonly tint: PaletteName
}

/** Total: an id nobody has ever heard of still gets a picture. */
export function presentArt(itemId: string): PresentArt {
  for (const [frag, icon, iconSet, tint] of ART_BY_FRAGMENT) {
    if (itemId.includes(frag)) return { icon, iconSet, tint }
  }
  return { icon: 'present', iconSet: 'visit', tint: 'flowerPink' }
}

/** The pickable table as a lookup. */
export function pickable(id: string): PresentDef | undefined {
  return PICKABLE.find((p) => p.id === id)
}
